import MyDrivePageContent from "../../components/organisms/MyDrivePageContent";
import { useQuery } from "@tanstack/react-query";
import { MY_COMPLETED_TRANSPORTATION_LIST } from "../../common/query-keys";
import { request } from "../../lib/api";
import { OrderType } from "../../interface/transportation";
import { MoreTemplates } from "../../components/template/MoreTemplates";
import NotFoundPageContent from "../../components/organisms/NotFoundPageContent";
import { ResponseType } from "../../types/response.types";
import { useNavigate } from "react-router";
import DateFilter from "../../components/atoms/DateFilter";
import { useState } from "react";
import dayjs from "dayjs";
// ✅ 스타일 지원 버전으로 교체
import XLSX from "xlsx-js-style";
import AlertModal from "../../components/molcules/Modal/AlertModal";

const MyDriveListPage = () => {
  /** ============================= state 영역 ============================= */
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelRequest, setIsCancelRequest] = useState(false);

  /** ============================= API 영역 ============================= */
  /** 내운송 기록 리스트 ( = 운송 완료 리스트 ) */
  const { data: myCompletedTransportationData } = useQuery({
    queryKey: [MY_COMPLETED_TRANSPORTATION_LIST, currentDate],
    queryFn: async () => {
      const result = await request<ResponseType<OrderType[]>>({
        method: "GET",
        url: MY_COMPLETED_TRANSPORTATION_LIST,
        params: {
          year: currentDate.year(),
          month: currentDate.month() + 1,
        },
      });

      return result.data;
    },
  });

  /** ============================= 비즈니스 로직 영역 ============================= */

  const handleDateChange = (date: dayjs.Dayjs) => {
    setCurrentDate(date);
  };

  const onDetailPageMoveHandler = (data: OrderType) => {
    navigate("/completedMatchListDetail", {
      state: { item: data },
    });
  };

  // 안드로이드 웹뷰 환경 감지 (필요 시 로직 교체)
  const isAndroidWebView = () => {
    // 웹뷰에서 다운시 false 로 변경
    return true;
  };

  // 안드로이드 네이티브로 파일 다운로드 요청
  const downloadFileForAndroid = (base64Data: string, fileName: string) => {
    try {
      if ((window as any).Android && (window as any).Android.downloadFile) {
        (window as any).Android.downloadFile(
          base64Data,
          fileName,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
      } else {
        window.postMessage(
          {
            type: "DOWNLOAD_FILE",
            data: {
              base64Data,
              fileName,
              mimeType:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            },
          },
          "*"
        );
      }
    } catch (error) {
      console.error("안드로이드 파일 다운로드 오류:", error);
      alert("파일 다운로드를 위해 브라우저에서 열어주세요.");
    }
  };

  // 브라우저에서 파일 다운로드
  const downloadFileForBrowser = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /** Excel 다운로드 */
  const exportToCSV = async (data: OrderType[]) => {
    let formattedData: any[] = [];

    if (!data?.[0]) {
      console.error("데이터가 없습니다.");
      return;
    }

    // timeSlots 배열을 기반으로 데이터 생성
    formattedData =
      data?.map((item: OrderType, idx: number) => {
        return {
          NO: idx + 1,
          운송일: dayjs(item.completed_date).format("MM-DD") + " ",
          수출입: item.import_export === 1 ? "수입" : "수출",
          컨테이너상차: item.load_place_name,
          작업지: item.work_place_name,
          컨테이너하차: item.unload_place_name,
          차량번호: item.driver_vehicle_number,
          컨테이너번호: item.container_no,
          컨테이너규격: item.container_spec,
          공급가액: item.final_settlement_amount,
          비고: "",
        };
      }) || [];

    // 워크시트 생성
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // 범위 계산
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

    // 공통 스타일 프리셋
    const borderThin = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    const dataAlign = { horizontal: "center", vertical: "center" };

    // ===== 1) 전체 셀 테두리 + 데이터 정렬 기본 적용
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[addr]) worksheet[addr] = { v: "" };
        worksheet[addr].s = {
          border: borderThin,
          alignment: dataAlign,
        };
      }
    }

    // ===== 2) 헤더(첫 번째 row)에 연한 회색 배경 + 볼드
    const headerRow = range.s.r; // 보통 0
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r: headerRow, c });
      const prev = worksheet[addr]?.s || {};
      worksheet[addr].s = {
        ...prev,
        font: { bold: true, sz: 12, color: { rgb: "000000" } },
        fill: {
          patternType: "solid",
          fgColor: { rgb: "E8E8E8" }, // 연한 회색
        },
        alignment: { horizontal: "center", vertical: "center" },
        border: borderThin,
      };
    }

    // ===== 3) 컬럼 너비 자동 조정
    const colWidths = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      let maxLen = 0;
      for (let r = range.s.r; r <= range.e.r; r++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const v = worksheet[addr]?.v;
        const s = v === undefined || v === null ? "" : String(v);
        maxLen = Math.max(maxLen, s.length);
      }
      colWidths.push({ wch: Math.min(Math.max(maxLen + 2, 10), 30) });
    }
    (worksheet as any)["!cols"] = colWidths;

    // 워크북 생성 및 시트 추가
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `${currentDate.year()}년 ${currentDate.month() + 1}월 운송 내역`
    );

    const fileName = `${currentDate.year()}년 ${
      currentDate.month() + 1
    }월 운송 내역.xlsx`;

    // 안드로이드/브라우저 분기
    if (isAndroidWebView()) {
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true, // xlsx-js-style에선 없어도 되지만 명시 유지
      });
      const base64Data = btoa(
        String.fromCharCode.apply(null, Array.from(new Uint8Array(excelBuffer)))
      );
      downloadFileForAndroid(base64Data, fileName);
    } else {
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadFileForBrowser(blob, fileName);
    }

    setIsLoading(false);
  };

  /** 내역 다운로드 버튼 클릭시 모달 표시 */
  const onActionButtonClick = async () => {
    setIsCancelRequest(!isCancelRequest);
  };

  /** 월별 운송 내역 다운로드 함수 */
  const onDownloadDriverHistory = async () => {
    if (
      !myCompletedTransportationData ||
      myCompletedTransportationData?.length === 0
    ) {
      alert("내역 데이터가 없습니다.");
      setIsCancelRequest(false);
      return;
    }

    setIsLoading(true);
    try {
      await exportToCSV(myCompletedTransportationData);
    } catch (error) {
      console.error("CSV 다운로드 중 오류 발생:", error);
      alert("다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  /** ============================= 컴포넌트 영역 ============================= */

  return (
    <MoreTemplates
      pageTitle="내 운송기록"
      pageHeaderTitle="설정"
      padding="0"
      gap="0"
      showActionButton={true}
      actionButtonText="내역 다운로드"
      onActionButtonClick={onActionButtonClick}
    >
      <>
        <DateFilter currentDate={currentDate} onDateChange={handleDateChange} />
        {myCompletedTransportationData?.length !== 0 ? (
          <MyDrivePageContent
            myCompletedTransportationData={myCompletedTransportationData || []}
            onDetailPageMoveHandler={onDetailPageMoveHandler}
          />
        ) : (
          <NotFoundPageContent contentText="운송한 기록이 없습니다." />
        )}
        {isCancelRequest && (
          <AlertModal
            setIsModal={setIsCancelRequest}
            title="알림"
            description={
              <>
                {`${currentDate.year()}년 ${
                  currentDate.month() + 1
                }월 운송 내역을 \n 다운로드 하시겠습니까?`}
              </>
            }
            onSubmit={onDownloadDriverHistory}
          />
        )}
      </>
    </MoreTemplates>
  );
};

export default MyDriveListPage;
